import { BaseEntity } from './base.model';

/**
 * Feature flag configuration per organization
 */
export interface FeatureFlag extends BaseEntity {
  organization_id: string;
  feature_code: FeatureCode;
  is_enabled: boolean;
  settings: FeatureSettings;
  enabled_at?: string;
  enabled_by?: string;
}

/**
 * Feature codes constant - single source of truth
 * Use FEATURE_CODES.INVENTORY instead of 'inventory' throughout the app
 */
export const FEATURE_CODES = {
  INVENTORY: 'inventory',
  DISCOUNTS: 'discounts',
  SHIFTS: 'shifts',
  KITCHEN_STATIONS: 'kitchen_stations',
  EMAIL_RECEIPTS: 'email_receipts',
  ORDER_STATUS_SMS: 'order_status_sms',
  ESTIMATES: 'estimates',
  BRANCH_PRODUCTS: 'branch_products',
  REPORTS: 'reports',
  MULTI_BUSINESS_TYPE: 'multi_business_type',
  DELIVERY: 'delivery',
  CUSTOM_PERMISSIONS: 'custom_permissions',
  RESERVATIONS: 'reservations',
  TIME_ATTENDANCE: 'time_attendance',
  POS_ORDERING: 'pos_ordering',
} as const;

/**
 * Available feature codes (derived from FEATURE_CODES constant)
 */
export type FeatureCode = (typeof FEATURE_CODES)[keyof typeof FEATURE_CODES];

/**
 * Feature-specific settings union type
 */
export type FeatureSettings =
  | InventoryFeatureSettings
  | DiscountsFeatureSettings
  | DeliveryFeatureSettings
  | ShiftFeatureSettings
  | TimeAttendanceFeatureSettings
  | Record<string, unknown>;

/**
 * Inventory feature settings
 */
export interface InventoryFeatureSettings {
  out_of_stock_behavior: OutOfStockBehavior;
  low_stock_alerts: boolean;
  low_stock_threshold: number; // Show "Only X left" when remaining_servings <= this. Default: 10
  transfer_enabled?: boolean; // Enable branch-to-branch stock transfers. Default: false
}

/**
 * Discounts feature settings
 */
export interface DiscountsFeatureSettings {
  manual_discounts_enabled: boolean;
}

export const DEFAULT_DISCOUNTS_SETTINGS: DiscountsFeatureSettings = {
  manual_discounts_enabled: false,
};

export interface DeliveryFeatureSettings {
  payment_channel_label: string;
  payment_number: string;
  payment_qr_url?: string;
  minimum_order_amount: number;
  delivery_fee_info: string;
}

export const DEFAULT_DELIVERY_SETTINGS: DeliveryFeatureSettings = {
  payment_channel_label: 'GCash',
  payment_number: '',
  minimum_order_amount: 0,
  delivery_fee_info: 'To be confirmed by seller',
};

/**
 * Cash shift management settings
 */
export type ShiftEnforcementMode = 'strict' | 'soft' | 'off';

export interface ShiftFeatureSettings {
  enforcement_mode: ShiftEnforcementMode;
}

export const DEFAULT_SHIFT_SETTINGS: ShiftFeatureSettings = {
  enforcement_mode: 'soft',
};

export const SHIFT_ENFORCEMENT_MODES: {
  value: ShiftEnforcementMode;
  label: string;
  description: string;
}[] = [
  {
    value: 'off',
    label: 'Off',
    description:
      'No enforcement. Cashiers can take orders without an open shift and no warning is shown.',
  },
  {
    value: 'soft',
    label: 'Soft (warn only)',
    description:
      'Cashiers see a warning when transacting without an open shift, but orders go through.',
  },
  {
    value: 'strict',
    label: 'Strict (block POS)',
    description:
      'POS order creation is blocked unless the cashier has an open shift on the current branch.',
  },
];

/**
 * Time and attendance settings
 */
export interface TimeAttendanceFeatureSettings {
  enable_camera_audit?: boolean;
  enable_face_recognition?: boolean;
  allowed_punch_methods?: ('pin' | 'face' | 'biometric')[];
  default_punch_mode?: 'pin' | 'face' | 'biometric';
  dahua_webhook_secret?: string;
  auto_flag_overnight_hours?: number;
  grace_period_mins?: number;
  break_duration_mins?: number;
}

export const DEFAULT_TIME_ATTENDANCE_SETTINGS: TimeAttendanceFeatureSettings = {
  enable_camera_audit: false,
  enable_face_recognition: true,
  allowed_punch_methods: ['pin', 'face', 'biometric'],
  default_punch_mode: 'pin',
  auto_flag_overnight_hours: 16,
  grace_period_mins: 15,
  break_duration_mins: 60,
};

/**
 * Out of stock behavior options
 */
export type OutOfStockBehavior = 'strict' | 'hide' | 'allow';

/**
 * Out of stock behavior descriptions for UI
 */
export const OUT_OF_STOCK_BEHAVIORS: {
  value: OutOfStockBehavior;
  label: string;
  description: string;
}[] = [
  {
    value: 'hide',
    label: 'Hide from menu',
    description: 'Products with insufficient ingredients will be hidden from the customer menu.',
  },
  {
    value: 'strict',
    label: 'Block ordering',
    description:
      'Orders will be rejected if any item has insufficient ingredients. Customers see an error.',
  },
  {
    value: 'allow',
    label: 'Allow ordering (track shortage)',
    description: 'Orders proceed even with insufficient stock. Shortages are logged for analytics.',
  },
];

/**
 * Default inventory settings
 */
export const DEFAULT_INVENTORY_SETTINGS: InventoryFeatureSettings = {
  out_of_stock_behavior: 'allow',
  low_stock_alerts: true,
  low_stock_threshold: 10,
  transfer_enabled: false,
};

/**
 * Registry entry for a feature flag — single source of truth for everything
 * the Feature Flags admin page needs to render and wire up a flag.
 *
 * Adding a new flag = adding ONE entry to FEATURE_REGISTRY below. The
 * feature-flags page iterates this array to render cards, status rows, and
 * form controls. No more 9-step checklist.
 *
 * If a flag needs custom settings UI (like inventory's out-of-stock dropdown),
 * set `hasCustomSettings: true` and slot a child component into the card via
 * the `[settings]` content projection in feature-flags.html.
 */
export interface FeatureRegistryEntry {
  /** Stable identifier — must match the value stored in organization_feature_flags.feature_code */
  code: FeatureCode;
  /** Human-readable feature name shown in the card header */
  name: string;
  /** One-line description shown under the name */
  description: string;
  /** PrimeIcons class (with the `pi-` prefix), e.g. `'pi-box'` */
  icon: string;
  /**
   * When true, the flag ignores subscription-tier gating and is always
   * available to toggle. Default: false (tier-gated).
   */
  alwaysAvailable?: boolean;
  /**
   * When true, only a super admin may change this org's toggle. Org admins see
   * the card and its current state but cannot flip it.
   *
   * Distinct from `alwaysAvailable` (tier entitlement) and from `defaultEnabled`
   * (the value with no row). This is about WHO may change it, not what it costs
   * or where it starts.
   *
   * Set on `pos_ordering` because that toggle converts the whole product: off
   * turns Qwentro into a standalone HR/inventory platform. An HR-only org's
   * Owner flipping it back on helps themselves to the entire POS, and an ordinary
   * org flipping it off locks itself out of its own register. Neither is a
   * routine self-serve setting — plan 69 §5 already documents the conversion as
   * a super-admin procedure.
   *
   * Enforced server-side too: UI-only gating would be cosmetic, because the
   * `organization_feature_flags` RLS policies accept any writer holding
   * `organization:admin` (Owner, Admin and Operations Manager all do). See
   * `organization_feature_flags_block_protected()`.
   */
  superAdminOnly?: boolean;
  /**
   * Value used when the org has NO row in `organization_feature_flags` for this
   * code. Signup creates no flag rows, so this is what every brand-new org gets.
   * Default: false (opt-in). Set true only for features that are part of the
   * baseline product and must work before anyone visits /u/feature-flags.
   *
   * Turning a `defaultEnabled` feature OFF writes an explicit `is_enabled: false`
   * row — see `FeatureFlagService.disableFeature()`.
   */
  defaultEnabled?: boolean;
  /**
   * Hide the entire card AND status row when this predicate returns true.
   * Used for kitchen_stations on retail-only orgs.
   */
  hideWhen?: (ctx: { isRetailOrg: boolean }) => boolean;
  /** Optional manage page — rendered as a "Manage →" link in the card body when enabled */
  manageRoute?: string;
  /** Short text shown when the feature is enabled */
  enabledHelp?: string;
  /** Short text shown when the feature is disabled */
  disabledHelp?: string;
  /** Short label for the right-column status row. Defaults to `name` if omitted */
  statusLabel?: string;
  /** Marks features that need a custom settings panel rendered inside the card body */
  hasCustomSettings?: boolean;
}

/**
 * Single source of truth for all feature flags shown on the Feature Flags
 * admin page. Order here = order rendered in the UI.
 */
export const FEATURE_REGISTRY: readonly FeatureRegistryEntry[] = [
  {
    code: 'inventory',
    name: 'Ingredient Inventory',
    description: 'Track raw materials, link recipes to products, auto-deduct on orders.',
    icon: 'pi-box',
    hasCustomSettings: true,
    enabledHelp: 'Manage ingredients and recipes from the Inventory page in the Configuration menu.',
    disabledHelp: 'Enable this feature to track ingredient stock and recipe-based deductions.',
    manageRoute: '/u/inventory',
  },
  {
    code: 'discounts',
    name: 'Discounts & Vouchers',
    description: 'Create discount codes, vouchers, and apply senior/PWD discounts.',
    icon: 'pi-percentage',
    hasCustomSettings: true,
    statusLabel: 'Discounts & Vouchers',
    enabledHelp: 'Manage discount codes, vouchers, and senior/PWD discounts from the Discounts page in the Configuration menu.',
    disabledHelp: 'Enable this feature to create voucher codes, apply senior/PWD discounts, and offer promotions to your customers.',
    manageRoute: '/u/discounts',
  },
  {
    code: 'shifts',
    name: 'Cash Shift Management',
    description: 'Track per-cashier shifts, cash reconciliation, and payment attribution.',
    icon: 'pi-clock',
    hasCustomSettings: true,
    statusLabel: 'Cash Shifts',
    enabledHelp: 'Cashiers can open and close shifts from the POS. Review history from the Shift History page.',
    disabledHelp: 'Enable this feature to track per-cashier shifts and cash reconciliation.',
    manageRoute: '/u/shift-history',
  },
  {
    code: 'kitchen_stations',
    name: 'Kitchen Stations',
    description: 'Route order items to designated prep areas (Food, Drinks, Desserts) with per-item tracking.',
    icon: 'pi-th-large',
    hideWhen: (ctx) => ctx.isRetailOrg,
    enabledHelp: 'Configure stations per branch from the Branches page.',
    disabledHelp: 'Enable this feature to split kitchen orders across multiple prep stations.',
    manageRoute: '/u/branches',
  },
  {
    code: 'email_receipts',
    name: 'Email Receipts',
    description: 'Send automated email receipts to customers after successful payment.',
    icon: 'pi-envelope',
    enabledHelp: 'Customers who provide an email at checkout will receive a receipt automatically.',
    disabledHelp: 'Enable this feature to send branded email receipts after every successful payment.',
  },
  {
    code: 'order_status_sms',
    name: 'Order Status SMS',
    description: 'Send real-time SMS updates to customers as their order moves through the queue.',
    icon: 'pi-comment',
    enabledHelp: 'Customers will receive SMS notifications when their order status changes.',
    disabledHelp: 'Enable this feature to keep customers informed via SMS as their order is prepared.',
  },
  {
    code: 'estimates',
    name: 'Estimates & Quotes',
    description: 'Create quotes for customers. Print and convert to orders when accepted.',
    icon: 'pi-file-edit',
    enabledHelp: 'Create and manage quotes from the Estimates page in the Configuration menu.',
    disabledHelp: 'Enable this feature to draft quotes that customers can accept and convert into orders.',
    manageRoute: '/u/estimates',
  },
  {
    code: 'branch_products',
    name: 'Branch Products',
    description:
      'Control which products are available at each branch. When disabled, all products are available at all branches.',
    icon: 'pi-box',
    enabledHelp: 'Set per-branch availability from each product\'s detail page.',
    disabledHelp: 'Enable this feature to restrict products to specific branches.',
  },
  {
    code: 'reports',
    name: 'Advanced Reports',
    description: 'Detailed analytics, comparison charts, CSV exports, and drill-down reports.',
    icon: 'pi-chart-bar',
    enabledHelp: 'Access advanced analytics from the Reports page.',
    disabledHelp: 'Enable this feature for detailed analytics, comparison charts, and CSV exports.',
    manageRoute: '/u/reports',
  },
  {
    code: 'multi_business_type',
    name: 'Multi Business Type',
    description:
      'Allow branches to have different business types (restaurant, retail, general).',
    icon: 'pi-building',
    alwaysAvailable: true,
    enabledHelp:
      'When creating a branch, you can pick its business type. Each branch is independent.',
    disabledHelp:
      'All branches automatically inherit this organization\'s business type. The branch form does not show a business type selector.',
  },
  {
    code: 'delivery',
    name: 'Delivery Orders',
    description:
      'Accept delivery orders via a dedicated QR code. Customers submit their address and payment proof; you approve or reject from the order queue.',
    icon: 'pi-truck',
    alwaysAvailable: true,
    hasCustomSettings: true,
    manageRoute: '/u/delivery-orders',
    enabledHelp:
      'A dedicated Delivery QR link is available from the Branches page. Configure your GCash/QRPh payment details.',
    disabledHelp:
      'Enable this feature to accept delivery orders through a dedicated QR code. You will need to configure a GCash or QRPh number for payment collection.',
  },
  {
    code: 'custom_permissions',
    name: 'Custom Permissions',
    description: 'Grant individual users additional permissions on top of their role.',
    icon: 'pi-shield',
    alwaysAvailable: true,
    statusLabel: 'Custom Permissions',
    enabledHelp:
      'Admins with member:manage can grant additional permissions to individual users from the Edit User modal. Targets must reload to apply changes.',
    disabledHelp:
      'Enable this feature to override permissions per-user on top of their role.',
  },
  {
    code: 'reservations',
    name: 'Reservations',
    description:
      'Let staff manage table reservations via an interactive timetable grid. Supports auto-assignment by party size.',
    icon: 'pi-calendar',
    alwaysAvailable: true,
    statusLabel: 'Reservations',
    manageRoute: '/u/reservations',
    enabledHelp:
      'Manage table reservations from the Reservations page. Configure slot duration per branch in Branch settings.',
    disabledHelp:
      'Enable this feature to allow staff to create and manage table reservations with a visual timetable grid.',
  },
  {
    code: 'time_attendance',
    name: 'Time & Attendance',
    description: 'Track staff work hours, Dahua biometric punches, PIN clock-in/out, and timesheets.',
    icon: 'pi-clock',
    // Deliberately NOT alwaysAvailable. HR & Payroll is entitlement-gated: it
    // ships with the Enterprise tier, and any other org needs a super admin to
    // grant it via features_override on /u/feature-flags. While it was
    // alwaysAvailable, isEnabled() skipped the subscription check entirely and
    // any Owner or Admin could switch HR on for themselves — it belonged to no
    // tier, so it was reachable without ever being sold.
    hasCustomSettings: true,
    manageRoute: '/u/attendance',
    enabledHelp:
      'Staff can clock in/out using PIN codes, Dahua biometric devices, or the touch time clock.',
    disabledHelp:
      'Enable this feature to track employee attendance, work hours, and timesheets.',
  },
  {
    code: 'pos_ordering',
    name: 'POS & Ordering',
    description:
      'Enable POS register, table layouts, kitchen displays, and order queue. Disable for standalone HR & Payroll mode.',
    icon: 'pi-calculator',
    alwaysAvailable: true,
    // POS is the baseline product — every org that signs up gets it without
    // anyone having to switch it on. Standalone HR/inventory orgs are the
    // exception and are configured by a super admin (see docs/plans/69).
    defaultEnabled: true,
    // Bundled in every tier, but not self-serve to flip. See superAdminOnly.
    superAdminOnly: true,
    statusLabel: 'POS & Ordering',
    manageRoute: '/u/pos',
    enabledHelp:
      'Cashiers and staff can take customer orders, manage tables, and use the POS terminal.',
    disabledHelp:
      'Disabling hides POS, tables, and ordering, converting Qwentro into a standalone HR/payroll or inventory platform.',
  },
];

/**
 * Backward-compatible map derived from FEATURE_REGISTRY. Existing callers
 * (e.g. the super-admin override panel) iterate `Object.entries(FEATURE_DEFINITIONS)`
 * — leaving this in place keeps them working without changes.
 */
export const FEATURE_DEFINITIONS: Record<
  FeatureCode,
  { name: string; description: string; icon: string }
> = Object.fromEntries(
  FEATURE_REGISTRY.map((f) => [f.code, { name: f.name, description: f.description, icon: f.icon }]),
) as Record<FeatureCode, { name: string; description: string; icon: string }>;

/**
 * Input type for creating/updating feature flags
 */
export interface FeatureFlagInput {
  organization_id: string;
  feature_code: FeatureCode;
  is_enabled: boolean;
  settings?: FeatureSettings;
}
